/**
 * Shared Performance-tab visual primitives (ADR-110 / ADR-109) — ONE primitive,
 * MANY callers (Structure Guide §9). Built to the LOCKED mocks; consumed by the
 * ops Performance tab (portal-staff) AND copy-forked into the subscriber modals
 * (portal-subscriber, S6). Every component takes plain numeric/array props and
 * imports nothing portal-specific, so the fork is a straight file copy.
 *
 * All figures are computed from real pipe data; where a datum is not yet
 * available (a pre-deploy additive column) the component degrades visibly rather
 * than inventing a shape.
 */
import './telemetry-perf.css';

// ── Layered loader (skeleton + text, in tandem — S7 standing directive) ──
export function PerfSkeleton({ label, rows = 2 }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-live="polite">
      <div className="telp-loadtext">
        <span className="telp-skel-spin" aria-hidden>⏳</span>{label}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="telp-skeleton" key={i}>
          <div className="telp-skel-bar" style={{ width: '40%' }} />
          <div className="telp-skel-bar" />
          <div className="telp-skel-bar" />
        </div>
      ))}
    </div>
  );
}

// ── Arrival Rhythm — beacons per hour, silence visible as the bars stopping ──
export interface RhythmBar { hour: string; beacons: number }
export function ArrivalRhythm({ bars, silentHours }: { bars: RhythmBar[]; silentHours: number | null }) {
  const W = 480, H = 64, GAP = 3;
  const shown = bars.slice(-48); // last 48 hours (the mock's window)
  if (shown.length === 0) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="No arrival data yet">
        <text x={W / 2} y={H / 2} textAnchor="middle" fontSize="12" fill="#475569">rhythm data building…</text>
      </svg>
    );
  }
  const max = Math.max(1, ...shown.map((b) => b.beacons));
  const bw = (W - GAP * shown.length) / shown.length;
  // The trailing run of empty hours reads as the silence region (red overlay).
  let trailingEmpty = 0;
  for (let i = shown.length - 1; i >= 0 && shown[i].beacons === 0; i--) trailingEmpty++;
  const isSilent = silentHours != null && silentHours > 0;
  const silentX = (bw + GAP) * (shown.length - trailingEmpty);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Beacons per hour over 48 hours">
      <g fill={isSilent ? '#f87171' : '#34d399'}>
        {shown.map((b, i) => {
          const h = b.beacons === 0 ? 0 : Math.max(3, Math.round((b.beacons / max) * (H - 4)));
          return <rect key={i} x={(bw + GAP) * i} y={H - h} width={bw} height={h} />;
        })}
      </g>
      {isSilent && trailingEmpty > 0 && (
        <>
          <rect x={silentX} y={0} width={W - silentX} height={H} fill="#7f1d1d" opacity="0.18" />
          <text x={(silentX + W) / 2} y={H / 2 + 4} textAnchor="middle" fontSize="12" fontWeight="800" fill="#fca5a5">
            silent
          </text>
        </>
      )}
    </svg>
  );
}

// ── Speed gauge — bands + count badges + typical marker (Visitor Performance) ──
export interface SpeedBands { fast: number; ok: number; slow: number; verySlow: number }
/** Position (0..1 across the gauge) of a send time on the 4-band scale. The bands
 *  are equal-width visually (26/26/26/22%) but map non-linear ms thresholds, so a
 *  time is placed proportionally WITHIN its band. */
export function gaugePosition(ms: number): number {
  if (ms < 100) return (ms / 100) * 0.26;
  if (ms < 500) return 0.26 + ((ms - 100) / 400) * 0.26;
  if (ms < 2000) return 0.52 + ((ms - 500) / 1500) * 0.26;
  return Math.min(1, 0.78 + ((ms - 2000) / 2000) * 0.22);
}
export function SpeedGauge({ bands, typicalMs }: { bands: SpeedBands; typicalMs: number }) {
  const total = bands.fast + bands.ok + bands.slow + bands.verySlow;
  const markerPct = gaugePosition(typicalMs) * 100;
  // Centre each badge in its band.
  const badge = (n: number, cls: string, leftPct: number) =>
    n > 0 ? <span className={`telp-cnt ${cls}`} style={{ left: `${leftPct}%` }}>{n}</span> : null;
  return (
    <div className="telp-gzone">
      <span className="telp-flag" style={{ left: `${markerPct}%` }}>typical</span>
      <div className="telp-gauge">
        <i className="telp-b1" /><i className="telp-b2" /><i className="telp-b3" /><i className="telp-b4" />
        {total > 0 && (
          <>
            {badge(bands.fast, 'f', 13)}
            {badge(bands.ok, 'k', 39)}
            {badge(bands.slow, 's', 65)}
            {badge(bands.verySlow, 'p', 89)}
          </>
        )}
        <span className="telp-marker" style={{ left: `${markerPct}%` }} />
      </div>
      <div className="telp-bandlbls">
        <span style={{ left: 0, width: '26%' }}>&lt;100 ms · fast</span>
        <span style={{ left: '26%', width: '26%' }}>&lt;500 ms · ok</span>
        <span style={{ left: '52%', width: '26%' }}>&lt;2 s · slow</span>
        <span style={{ left: '78%', width: '22%' }}>&gt;2 s · very slow</span>
      </div>
    </div>
  );
}

// ── Trend sparkline (plain-language direction below it, drawn by the caller) ──
export function TrendSpark({ points, color }: { points: number[]; color: string }) {
  if (points.length < 2) return null;
  const W = 110, H = 26;
  const min = Math.min(...points), max = Math.max(...points);
  const span = max - min || 1;
  const path = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * W;
      const y = H - 2 - ((p - min) / span) * (H - 4);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="7-day trend">
      <polyline points={path} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  );
}

// ── Device mix bar ──
export interface DeviceShare { mobile: number; desktop: number; tablet: number }
export function DeviceMix({ share }: { share: DeviceShare }) {
  return (
    <>
      <div className="telp-mix" role="img" aria-label={`Device mix: ${share.mobile}% mobile, ${share.desktop}% desktop, ${share.tablet}% tablet`}>
        {share.mobile > 0 && <i style={{ width: `${share.mobile}%`, background: '#6366f1' }} />}
        {share.desktop > 0 && <i style={{ width: `${share.desktop}%`, background: '#34d399' }} />}
        {share.tablet > 0 && <i style={{ width: `${share.tablet}%`, background: '#64748b' }} />}
      </div>
      <span className="telp-s">
        <b style={{ color: '#f1f5f9' }}>{share.mobile}%</b> mobile ·{' '}
        <b style={{ color: '#f1f5f9' }}>{share.desktop}%</b> desktop ·{' '}
        <b style={{ color: '#f1f5f9' }}>{share.tablet}%</b> tablet
      </span>
    </>
  );
}

// ── Page-load-by-device bars with the single 3s benchmark line (D7/ADR-108) ──
export interface LoadRow { device: string; typicalMs: number; samples: number }
const BENCH_MS = 3000; // single sourced guideline, same line both devices (ADR-108/110)
const LOAD_SCALE_MS = 8000; // full bar width
export function LoadBars({ rows }: { rows: LoadRow[] }) {
  const benchPct = (BENCH_MS / LOAD_SCALE_MS) * 100;
  return (
    <>
      {rows.map((r) => {
        const widthPct = Math.min(100, (r.typicalMs / LOAD_SCALE_MS) * 100);
        const over = r.typicalMs > BENCH_MS;
        return (
          <div className="telp-lrow" key={r.device}>
            <span className="telp-dev">{r.device}</span>
            <span className="telp-lbar">
              <i className={over ? 'telp-l-bad' : 'telp-l-good'} style={{ width: `${widthPct}%` }} />
              <span className="telp-bench" style={{ left: `${benchPct}%` }} />
            </span>
            <span className={`telp-lval${over ? ' warn' : ''}`}>{(r.typicalMs / 1000).toFixed(1)} s typical</span>
          </div>
        );
      })}
    </>
  );
}
